import { useMemo } from 'react'
import { getModelXyzBounds, getScaledPaperLimits, getScaledPaperMarginLimits } from '@domainUtils'
import { useTheme } from '@mui/material'
import { useCameraStore } from '@modugen/ui-toolkit'
import { useControlStore, useModelStore } from '@editorStores'
import { useSceneLineMarker } from '@editorHooks'

const PLAN_AREA_Z_OFFSET = 0.05

export interface PlanAreaFrameLinesProps {
  planSettings: PdfPlanSettings | undefined
}

/**
 * Plan area frame (paper edge and print margin) of the given plan settings — the saved ones (see
 * `PlanAreaFrame`) or a form's unsaved values (see `usePlanSettingsScenePreview`).
 */
const PlanAreaFrameLines = ({ planSettings }: PlanAreaFrameLinesProps): null => {
  const { scenePalette } = useTheme()
  const showPlanArea = useModelStore(state => state.showPlanArea)
  const model = useModelStore(state => state.model)
  const isOrthographic = useCameraStore(state => state.isOrthographic)
  const activeStorey = useControlStore(state => state.floorplanState.storey)

  const frameZ = useMemo(() => {
    const xyzBounds = getModelXyzBounds(model)
    if (isOrthographic && activeStorey !== null) {
      const storeyLowerZ = model.storey_boundaries[activeStorey]?.[0]
      if (typeof storeyLowerZ === 'number') {
        return storeyLowerZ + PLAN_AREA_Z_OFFSET
      }
    }
    return xyzBounds.zMin + PLAN_AREA_Z_OFFSET
  }, [activeStorey, isOrthographic, model])

  const frameLines = useMemo(() => {
    if (!planSettings || !showPlanArea) return []

    const { xlim, ylim } = getScaledPaperLimits(planSettings)
    const [xMin, xMax] = xlim
    const [yMin, yMax] = ylim

    const bottomLeft = { x: xMin, y: yMin, z: frameZ }
    const bottomRight = { x: xMax, y: yMin, z: frameZ }
    const topRight = { x: xMax, y: yMax, z: frameZ }
    const topLeft = { x: xMin, y: yMax, z: frameZ }

    return [
      [bottomLeft, bottomRight],
      [bottomRight, topRight],
      [topRight, topLeft],
      [topLeft, bottomLeft],
    ]
  }, [frameZ, planSettings, showPlanArea])

  const marginLines = useMemo(() => {
    if (!planSettings || !showPlanArea) return []

    const { xlim, ylim } = getScaledPaperMarginLimits(planSettings)
    const [xMin, xMax] = xlim
    const [yMin, yMax] = ylim

    const bottomLeft = { x: xMin, y: yMin, z: frameZ }
    const bottomRight = { x: xMax, y: yMin, z: frameZ }
    const topRight = { x: xMax, y: yMax, z: frameZ }
    const topLeft = { x: xMin, y: yMax, z: frameZ }

    return [
      [bottomLeft, bottomRight],
      [bottomRight, topRight],
      [topRight, topLeft],
      [topLeft, bottomLeft],
    ]
  }, [frameZ, planSettings, showPlanArea])

  useSceneLineMarker({
    lines: frameLines,
    enabled: frameLines.length > 0,
    color: scenePalette.planArea.frame as string,
    lineWidth: 4,
    renderOrder: 1000,
    disableDepthTest: true,
  })

  useSceneLineMarker({
    lines: marginLines,
    enabled: marginLines.length > 0,
    color: scenePalette.planArea.margin as string,
    lineWidth: 2,
    dashed: true,
    dashSize: 0.2,
    gapSize: 0.1,
    renderOrder: 1001,
    disableDepthTest: true,
  })

  return null
}

export default PlanAreaFrameLines
